import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import useVaultItems from '../../hooks/useVaultItems';
import { Card, PillButton, Input, Modal, EmptyState } from '../../ui';
import { ReactComponent as FolderLockIcon } from '../../assets/icons/folder-lock.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/copy.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/arrow-left.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../../assets/icons/eye-off.svg';
import './AdminLayout.css';
import { apiErrorMessage } from '../../utils/apiError';

function Vaults() {
  const { t } = useTranslation();
  const { has, orgId } = useOutletContext();
  const canManage = has('vaults.manage');
  const canWrite = has('vaults.write');

  const [vaults, setVaults] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDeptId, setFormDeptId] = useState('');
  const [saving, setSaving] = useState(false);

  const [openVaultId, setOpenVaultId] = useState(null);
  const [vaultItems, setVaultItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [addSearch, setAddSearch] = useState('');

  // Vault personal del propio usuario, para el buscador de "añadir ítem".
  const { items: personalItems } = useVaultItems({ limit: 100 });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/org/${orgId}/vaults`),
      api.get(`/org/${orgId}/departments`),
    ])
      .then(([vRes, dRes]) => {
        setVaults(vRes.data.vaults || []);
        setDepartments(dRes.data.departments || []);
      })
      .catch((err) => setError(apiErrorMessage(err, t, 'errors.generic')))
      .finally(() => setLoading(false));
  }, [orgId, t]);

  useEffect(() => { load(); }, [load]);

  const deptName = useCallback((id) => (id ? departments.find((d) => d.id === id)?.name : null) || t('admin.vaults.global'), [departments, t]);

  const byDepartment = useMemo(() => {
    const groups = new Map();
    vaults.forEach((v) => {
      const key = v.departmentId || 'global';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(v);
    });
    return groups;
  }, [vaults]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post(`/org/${orgId}/vaults`, { name: formName, departmentId: formDeptId || null });
      setCreateOpen(false);
      setFormName('');
      setFormDeptId('');
      load();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVault = async (v) => {
    if (!window.confirm(t('admin.vaults.confirmDelete'))) return;
    try {
      await api.delete(`/org/${orgId}/vaults/${v.id}`);
      if (openVaultId === v.id) setOpenVaultId(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    }
  };

  const loadItems = useCallback((vaultId) => {
    setItemsLoading(true);
    api.get(`/org/${orgId}/vaults/${vaultId}/items`)
      .then((res) => setVaultItems(res.data.items || []))
      .catch((err) => setError(apiErrorMessage(err, t, 'errors.generic')))
      .finally(() => setItemsLoading(false));
  }, [orgId, t]);

  const openVault = (v) => { setOpenVaultId(v.id); setRevealed({}); setAddSearch(''); loadItems(v.id); };

  const addItem = async (itemId) => {
    try {
      await api.post(`/org/${orgId}/vaults/${openVaultId}/items`, { itemId });
      loadItems(openVaultId);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/org/${orgId}/vaults/${openVaultId}/items/${itemId}`);
      loadItems(openVaultId);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'errors.generic'));
    }
  };

  const copyValue = async (value) => {
    if (value) await navigator.clipboard.writeText(value);
  };

  const searchResults = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    if (!q) return [];
    const alreadyIn = new Set(vaultItems.map((i) => String(i.id)));
    return personalItems
      .filter((i) => !alreadyIn.has(String(i.id)) && i.title.toLowerCase().includes(q))
      .slice(0, 8);
  }, [addSearch, personalItems, vaultItems]);

  if (loading) return <p>{t('common.loading')}</p>;

  const openVault_ = vaults.find((v) => v.id === openVaultId);

  if (openVault_) {
    return (
      <div>
        <div className="sgc-admin__section-header">
          <button type="button" className="sgc-admin__icon-btn" onClick={() => setOpenVaultId(null)}>
            <ArrowLeftIcon width={16} height={16} aria-hidden="true" /> {t('common.back')}
          </button>
        </div>
        <h2 className="sgc-admin__section-title">{openVault_.name}</h2>
        <p className="sgc-admin__muted">{deptName(openVault_.departmentId)}</p>

        {error && <p className="sgc-admin__error">{error}</p>}

        {canWrite && (
          <Card className="sgc-admin__form">
            <Input
              icon={SearchIcon}
              label={t('admin.vaults.addItemSearch')}
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
            />
            {searchResults.length > 0 && (
              <div>
                {searchResults.map((i) => (
                  <div key={i.id} className="sgc-admin__dept-row" style={{ borderBottom: 'none', padding: 'var(--space-2) 0' }}>
                    <span style={{ flex: 1 }}>{i.title}</span>
                    <PillButton size="sm" variant="secondary" icon={PlusIcon} onClick={() => addItem(i.id)}>
                      {t('admin.vaults.addItem')}
                    </PillButton>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card padded={false}>
          {itemsLoading ? (
            <p style={{ padding: 'var(--space-5)' }}>{t('common.loading')}</p>
          ) : vaultItems.length === 0 ? (
            <EmptyState icon={FolderLockIcon} title={t('admin.vaults.emptyItemsTitle')} description={t('admin.vaults.emptyItemsDescription')} />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('admin.vaults.col.password')}</th>
                  <th>{t('admin.vaults.col.addedBy')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {vaultItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.name}</strong>
                      {item.description && <div className="sgc-admin__muted">{item.description}</div>}
                    </td>
                    <td>
                      <div className="sgc-admin__row-actions">
                        <span style={{ fontFamily: 'var(--font-mono)' }}>
                          {revealed[item.id] ? (item.data?.password || '—') : '••••••••'}
                        </span>
                        <button type="button" className="sgc-admin__icon-btn" title={t('dashboard.reveal')} onClick={() => setRevealed((p) => ({ ...p, [item.id]: !p[item.id] }))}>
                          {revealed[item.id] ? <EyeOffIcon width={14} height={14} aria-hidden="true" /> : <EyeIcon width={14} height={14} aria-hidden="true" />}
                        </button>
                        <button type="button" className="sgc-admin__icon-btn" onClick={() => copyValue(item.data?.password)} title={t('dashboard.copy')}>
                          <CopyIcon width={14} height={14} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                    <td className="sgc-admin__muted">{new Date(item.addedAt).toLocaleDateString()}</td>
                    <td>
                      <button type="button" className="sgc-admin__icon-btn sgc-admin__icon-btn--danger" onClick={() => removeItem(item.id)} title={t('common.remove')}>
                        <TrashIcon width={16} height={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="sgc-admin__section-header">
        <h2 className="sgc-admin__section-title">{t('admin.tabs.vaults')}</h2>
        {canManage && <PillButton icon={PlusIcon} onClick={() => setCreateOpen(true)}>{t('admin.vaults.add')}</PillButton>}
      </div>

      {error && <p className="sgc-admin__error">{error}</p>}

      {vaults.length === 0 ? (
        <Card><EmptyState icon={FolderLockIcon} title={t('admin.vaults.emptyTitle')} description={t('admin.vaults.emptyDescription')} /></Card>
      ) : (
        Array.from(byDepartment.entries()).map(([key, list]) => (
          <div key={key}>
            <h3 className="sgc-admin__muted" style={{ margin: '0 0 8px' }}>{key === 'global' ? t('admin.vaults.global') : deptName(list[0].departmentId)}</h3>
            <Card padded={false}>
              {list.map((v) => (
                <div key={v.id} className="sgc-admin__dept-row">
                  <FolderLockIcon width={16} height={16} aria-hidden="true" />
                  <button type="button" style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer', font: 'inherit' }} onClick={() => openVault(v)}>
                    {v.name}
                  </button>
                  {canManage && (
                    <button type="button" className="sgc-admin__icon-btn sgc-admin__icon-btn--danger" onClick={() => handleDeleteVault(v)} title={t('common.delete')}>
                      <TrashIcon width={16} height={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </Card>
          </div>
        ))
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t('admin.vaults.add')}>
        <form className="sgc-admin__form" onSubmit={handleCreate}>
          <Input label={t('common.name')} value={formName} onChange={(e) => setFormName(e.target.value)} required />
          <select className="sgc-admin__select" value={formDeptId} onChange={(e) => setFormDeptId(e.target.value)}>
            <option value="">{t('admin.vaults.global')}</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <div className="sgc-admin__form-actions">
            <PillButton type="submit" disabled={saving || !formName.trim()}>
              {saving ? t('common.loading') : t('common.save')}
            </PillButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Vaults;
